"use client";

import { Alert, Button, Link } from "@softmax/design-system";
import { colors, spacing } from "@softmax/design-system/tokens.stylex";
import * as stylex from "@stylexjs/stylex";
import { type ReactNode } from "react";
import type { CoworldOption, PrivateRunDraft } from "./coworld-records";
import { coworldLayout } from "./coworld-layout.styles";
import { CoworldRunFields } from "./coworld-run-fields";

export function CoworldRunForm({
  draft,
  variants,
  bounds,
  onChange,
  playerPickers,
  disabled,
  pending,
  error,
  onSubmit,
  onNewRun,
  cancelHref,
}: Readonly<{
  draft: PrivateRunDraft;
  variants: readonly CoworldOption[];
  bounds: Readonly<{ min: number; max: number }>;
  onChange(changes: Partial<PrivateRunDraft>): void;
  playerPickers: readonly ReactNode[];
  disabled: boolean;
  pending: boolean;
  error: string | null;
  onSubmit(): void;
  onNewRun(): void;
  cancelHref: string;
}>) {
  const total = draft.playerCount * draft.episodes * draft.spendLimitUsd;
  return (
    <form
      {...stylex.props(styles.composer)}
      onSubmit={(event) => {
        event.preventDefault();

        if (!pending && !disabled) onSubmit();
      }}
    >
      <CoworldRunFields
        draft={draft}
        variants={variants}
        bounds={bounds}
        onChange={onChange}
        playerPickers={playerPickers}
        disabled={pending || !!error}
      />
      {error && (
        <Alert tone="danger">{error} Retry uses the same run request.</Alert>
      )}
      <div {...stylex.props(styles.submit)}>
        <div>
          <p {...stylex.props(coworldLayout.heading)}>
            {Number.isFinite(total) ? `$${total.toFixed(2)}` : "—"} total LLM
            limit
          </p>
          <p {...stylex.props(coworldLayout.muted)}>
            {draft.playerCount} players × {draft.episodes} games × $
            {draft.spendLimitUsd}
          </p>
        </div>
        <div {...stylex.props(coworldLayout.inline)}>
          <Link href={cancelHref} focus="native">
            Cancel
          </Link>
          <Button
            type="submit"
            variant="primary"
            disabled={pending || disabled}
          >
            {pending ? "Starting…" : error ? "Retry run" : "Run privately"}
          </Button>
        </div>
      </div>
      {error && (
        <Button type="button" onClick={onNewRun}>
          Edit run setup
        </Button>
      )}
    </form>
  );
}
const styles = stylex.create({
  composer: {
    maxWidth: 720,
    width: "100%",
    marginInline: "auto",
    display: "grid",
    gap: spacing.space24,
  },
  submit: {
    position: "sticky",
    bottom: 0,
    backgroundColor: colors.background,
    paddingBlock: spacing.space16,
    borderTopWidth: 1,
    borderTopStyle: "solid",
    borderTopColor: colors.hairline,
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "center",
    gap: spacing.space12,
  },
});
